/**
 * OrderTagsForm.jsx — Modal form that captures shipping details for
 * the free-10-tags CTA on TapNfcSalesPage. Posts to
 * /api/tapnfc/orders → mints a dormant venue placeholder + emails
 * admin and customer via Resend.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

const ORANGE = "#ff6a13";
const ORANGE_DEEP = "#e04c00";
const INK = "#0f0a05";

export default function OrderTagsForm({ onClose, onSuccess }) {
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", business_name: "",
    tag_count: 10,
    shipping_address: {
      line1: "", line2: "", suburb: "", state: "QLD",
      postcode: "", country: "Australia",
    },
    notes: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm((f) => ({ ...f, shipping_address: { ...f.shipping_address, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/tapnfc/orders`, {
        ...form, source: "landing_modal",
      });
      setOk(data);
      toast.success(`Order confirmed — id ${data.order_id.slice(-6)}`);
      if (onSuccess) onSuccess(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(`Couldn't submit: ${JSON.stringify(msg).slice(0, 200)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="order-tags-modal" onClick={onClose}
         style={{
           position: "fixed", inset: 0, background: "rgba(15,10,5,0.6)",
           display: "flex", alignItems: "center", justifyContent: "center",
           zIndex: 1000, padding: 20,
         }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{
             background: "#fff", borderRadius: 20, maxWidth: 560,
             width: "100%", maxHeight: "90vh", overflow: "auto",
             boxShadow: "0 30px 80px rgba(0,0,0,0.35)",
             fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
           }}>
        <div style={{
          padding: "20px 24px",
          background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_DEEP} 100%)`,
          color: "#fff", borderRadius: "20px 20px 0 0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.1em", fontWeight: 800, textTransform: "uppercase", opacity: 0.9 }}>Free trial</div>
            <h2 style={{ margin: "4px 0 0", fontSize: 22, fontWeight: 900, letterSpacing: "-0.02em" }}>Order 10 HustleTap tags</h2>
          </div>
          <button onClick={onClose} data-testid="order-tags-close"
                  style={{ background: "rgba(0,0,0,0.25)", color: "#fff",
                           border: 0, width: 36, height: 36, borderRadius: 999,
                           fontSize: 18, cursor: "pointer" }}>×</button>
        </div>

        {ok ? (
          <div style={{ padding: 32, textAlign: "center" }} data-testid="order-tags-success">
            <div style={{ fontSize: 48, color: ORANGE }}>✓</div>
            <h3 style={{ margin: "12px 0 8px", fontSize: 22, color: INK }}>You're in.</h3>
            <p style={{ color: "#4a3c2c", fontSize: 15 }}>We just emailed confirmation to <strong>{form.email}</strong>. Tags ship from Brisbane in 48 hours.</p>
            <p style={{ color: "#94867a", fontSize: 12, marginTop: 16 }}>Order id: <code>{ok.order_id}</code></p>
            <button onClick={onClose} data-testid="order-tags-done"
                    style={{
                      marginTop: 20, padding: "12px 28px",
                      background: INK, color: "#fff", border: 0,
                      borderRadius: 999, fontWeight: 800, cursor: "pointer",
                    }}>Done</button>
          </div>
        ) : (
          <form onSubmit={submit} style={{ padding: 24, display: "grid", gap: 12 }}>
            <p style={{ margin: 0, color: "#4a3c2c", fontSize: 14 }}>
              Ships from Brisbane in 48 hours. No credit card. If they don't
              lift your review count in 30 days, we buy them back.
            </p>
            <Row><Field label="Your name" required value={form.name} onChange={(v) => set("name", v)} testid="order-name" /></Row>
            <Row>
              <Field label="Email" type="email" required value={form.email} onChange={(v) => set("email", v)} testid="order-email" />
              <Field label="Phone" type="tel" value={form.phone} onChange={(v) => set("phone", v)} testid="order-phone" />
            </Row>
            <Row><Field label="Business name" required value={form.business_name} onChange={(v) => set("business_name", v)} testid="order-business" /></Row>
            <div style={{ marginTop: 8, fontSize: 12, fontWeight: 800, color: "#6d5a44", letterSpacing: "0.05em", textTransform: "uppercase" }}>Ship to</div>
            <Row><Field label="Address line 1" required value={form.shipping_address.line1} onChange={(v) => setAddr("line1", v)} testid="order-line1" /></Row>
            <Row><Field label="Address line 2 (optional)" value={form.shipping_address.line2} onChange={(v) => setAddr("line2", v)} testid="order-line2" /></Row>
            <Row>
              <Field label="Suburb" required value={form.shipping_address.suburb} onChange={(v) => setAddr("suburb", v)} testid="order-suburb" />
              <Field label="State" required value={form.shipping_address.state} onChange={(v) => setAddr("state", v)} testid="order-state" />
              <Field label="Postcode" required value={form.shipping_address.postcode} onChange={(v) => setAddr("postcode", v)} testid="order-postcode" />
            </Row>
            <Row><Field label="Notes for our team (optional)" value={form.notes} onChange={(v) => set("notes", v)} testid="order-notes" multiline /></Row>
            <button type="submit" disabled={busy} data-testid="order-submit"
                    style={{
                      marginTop: 8, padding: "14px 24px",
                      background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_DEEP} 100%)`,
                      color: "#fff", border: 0, borderRadius: 999,
                      fontWeight: 800, fontSize: 15, cursor: busy ? "wait" : "pointer",
                      boxShadow: "0 10px 24px rgba(255,106,19,0.35)", opacity: busy ? 0.6 : 1,
                    }}>
              {busy ? "Sending…" : "Ship my 10 tags →"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: `repeat(${Array.isArray(children) ? children.length : 1}, 1fr)`, gap: 10 }}>{children}</div>;
}

function Field({ label, value, onChange, type = "text", required, testid, multiline }) {
  const Input = multiline ? "textarea" : "input";
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, fontWeight: 700, color: "#4a3c2c" }}>
      {label}{required && <span style={{ color: ORANGE_DEEP }}> *</span>}
      <Input value={value} onChange={(e) => onChange(e.target.value)} required={required} type={type}
             data-testid={testid} rows={multiline ? 3 : undefined}
             style={{
               padding: "10px 12px", border: "1px solid #d6c9b5",
               borderRadius: 8, fontSize: 14, fontFamily: "inherit",
               background: "#fff", color: INK,
             }} />
    </label>
  );
}
